/**
 * RD-ROLE-006 — the "also notify" setting for the Office Inbox: EXTRA user
 * ids that receive the "Pricing needed" digest email on top of the tenant
 * owner (server-resolved floor). Structurally a twin of
 * `estimateResponseSettings.js`'s `ESTIMATE_RESPONSE_NOTIFY_SETTINGS_KEY` —
 * same shape, same normalizer, same "adds, never subtracts" safety argument
 * — kept as its own module rather than folding into that file because the two
 * features are independent and unrelated saves must not risk cross-feature
 * merge conflicts.
 *
 * *** CROSS-RUNTIME CONTRACT. *** Read server-side by
 * `supabase/functions/_shared/officeTaskNotifySettings.ts`
 * (`OFFICE_TASK_NOTIFY_SETTINGS_KEY`, Task 2) — the stored key spelling is
 * wire format, not a local style choice. Change both sides in one commit.
 */

/** USER ids that receive the digest in addition to the tenant owner. */
export const OFFICE_TASK_NOTIFY_SETTINGS_KEY = 'office_task_notify_user_ids';

/** Mirrors `MAX_NOTIFY_EXTRA_USER_IDS` server-side (Task 2). */
export const MAX_OFFICE_TASK_NOTIFY_USER_IDS = 20;

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

/**
 * Canonical stored shape: uuid-shaped strings, lowercased, deduped, sorted,
 * capped. Sorting matters for the same reason as the estimate twin — the
 * branding form's dirty-check is a `JSON.stringify` compare, so an array
 * whose order depends on click sequence would read as an unsaved edit after
 * ticking and un-ticking the same person.
 */
export function normalizeOfficeTaskNotifyUserIds(value) {
  if (!Array.isArray(value)) return [];
  const out = new Set();
  for (const entry of value) {
    if (typeof entry !== 'string') continue;
    const trimmed = entry.trim();
    if (!UUID_RE.test(trimmed)) continue;
    out.add(trimmed.toLowerCase());
    if (out.size >= MAX_OFFICE_TASK_NOTIFY_USER_IDS) break;
  }
  return [...out].sort();
}

/**
 * Did THIS save change who else gets the digest? Normalized on both sides
 * before comparing (an array can never be compared with `!==`), matching
 * `diffEstimateResponseNotifyUsers`.
 */
export function diffOfficeTaskNotifyUsers(prior, next) {
  const before = normalizeOfficeTaskNotifyUserIds(prior?.[OFFICE_TASK_NOTIFY_SETTINGS_KEY]);
  const after = normalizeOfficeTaskNotifyUserIds(next?.[OFFICE_TASK_NOTIFY_SETTINGS_KEY]);
  if (before.join('|') === after.join('|')) return null;
  return {
    previous: before,
    user_ids: after,
    added: after.filter((id) => !before.includes(id)),
    removed: before.filter((id) => !after.includes(id)),
  };
}
